import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxSegmentedButtonModule } from '@flowx/angular-ui-toolkit'
import {
  LabelAccessibilityProps,
  NomenclatorValue,
  SegmentedButtonTemplateConfig,
  getStaticOptions,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-segmented-button-wrapper',
  imports: [
    CommonModule,
    FlxSegmentedButtonModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './segmented-button-wrapper.component.html',
  styleUrl: './segmented-button-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxSegmentedButtonWrapperComponent implements OnInit {
  #injector = inject(Injector)

  templateConfig = input.required<SegmentedButtonTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => ({
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
  }))

  // dynamic props
  label: Signal<string>
  helper: Signal<string>
  errorMessages: Record<string, Signal<string>>
  options: Signal<Array<{ label: string; value: string }>>
  ariaLabel: Signal<string>

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly itemId = `${this.elementUniqueId}-form-item`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const { label, helper } = this.flowxProps()
      this.label = getDynamicPropSignal(label, config)
      this.helper = getDynamicPropSignal(helper, config)
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = computed(() => this.accessibility()?.label)
      // Static options only, whatever sourceType says.
      this.options = toSignal(
        getStaticOptions(config.dataSource?.options as NomenclatorValue[] | undefined, config),
        { initialValue: [] }
      )
    })
  }
}
